import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletConnect } from '../hooks/useWalletConnect';
import { useAppSelector } from '../app/hooks';
import { selectToken } from '../data/accountSlice';
import { getImageUrl } from '../utils/assets';
import { getAgreements, agreeToAgreement, Agreement } from '../api/agreement';
import toast from 'react-hot-toast';
import Dialog from './Dialog';
import './AgreementModal.css';

interface AgreementModalProps {
  visible: boolean;
  onClose: () => void;
}

const AgreementModal: React.FC<AgreementModalProps> = ({ visible, onClose }) => {
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [canAgree, setCanAgree] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { isConnected, disconnectWallet } = useWalletConnect();
  const token = useAppSelector(selectToken);

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('xieyi/' + name)})`;
  const getBtnImg = (name: string) => `url(${getImageUrl('welcome/' + name)})`;

  useEffect(() => {
    if (visible) {
      fetchAgreements();
      setCanAgree(false);
    } else {
      // Reset state when closed
      setIsDropdownOpen(false);
      setSelectedId('all');
    }
  }, [visible]);

  // Handle scroll event
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Allow a small buffer (e.g., 5px) for floating point calculations
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setCanAgree(true);
    }
  };

  // Reset scroll position when content changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
      setCanAgree(false);
      
      // If content is short enough not to scroll, enable agree immediately
      if (contentRef.current.scrollHeight <= contentRef.current.clientHeight) {
        setCanAgree(true);
      }
    }
  }, [selectedId, agreements]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const data = await getAgreements();
      setAgreements(data);
      // Default to 'all' is already set in state initialization
    } catch (error) {
      console.error('Failed to fetch agreements', error);
      toast.error(t('errors.loadAgreementsFailed', 'Failed to load agreements'));
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementSelect = (id: string) => {
    setSelectedId(id);
    setIsDropdownOpen(false);
  };

  const handleAgree = async () => {
    if (!canAgree) return;

    // Check if logged in
    const isLoggedIn = isConnected && token;

    if (!isLoggedIn) {
      // Not logged in, cache agreement action
      // We assume if user agrees here, they agree to ALL or whatever is selected.
      // Requirement: "cached agreementId is all"
      localStorage.setItem('pending_agreement', 'all');
      toast.success(t('welcome.agreementCached', 'Agreement cached locally'));
      onClose();
      return;
    }

    // If logged in, proceed with API call
    try {
      // Requirement: "request with this agreementId = all" if cached, but here we are live.
      // If we follow the pattern, we should probably just send 'all' if selectedId is 'all'.
      // But if user selected specific one, maybe we should send that?
      // However, the offline logic forces 'all'. To be consistent, let's try to respect selection if live,
      // or if "All" is selected, send 'all' (if backend supports) or loop.
      // Given the offline instruction, it seems 'all' is a special key we might need to support or loop.
      // Let's stick to the previous logic for live: loop if 'all', or send ID.
      // Wait, if I send 'all' string to `agreeToAgreement`, it sends `{ agreementId: 'all' }`.
      
      if (selectedId === 'all') {
         await agreeToAgreement('all');
      } else {
         await agreeToAgreement(selectedId);
      }
      
      toast.success(t('welcome.agreementAccepted', 'Agreement accepted'));
      onClose();
    } catch (error) {
      console.error('Failed to agree', error);
      toast.error(t('errors.submitAgreementFailed', 'Failed to submit agreement'));
    }
  };

  const handleReject = () => {
    setShowRejectConfirm(true);
  };

  const confirmReject = async () => {
    if (isConnected) {
      await disconnectWallet();
    }
    localStorage.removeItem('pending_agreement');
    setShowRejectConfirm(false);
    onClose();
  };

  const selectedAgreement = selectedId === 'all' ? null : agreements.find(a => a.id === selectedId);
  const displayContent = selectedId === 'all' 
    ? agreements.map(a => `<h3>${a.title}</h3>${a.content}<br/><hr/><br/>`).join('') 
    : selectedAgreement?.content;

  if (!visible && agreements.length === 0 && !loading) return null;

  return (
    <div className={`agreement-modal-container ${visible ? 'visible' : ''}`}>
      <div className="agreement-backdrop" onClick={onClose} />

      <div className="agreement-content-wrapper">
        {/* Background Layer 
        <div className="agreement-bg-layer" style={{ backgroundImage: getImg('xieyi_background.png') }} />
        */}
        {/* Main Panel */}
        <div className="agreement-main-panel" style={{ backgroundImage: getImg('xieyi_main_panel.png') }}>
          {/* Close Button 
          <div 
            className="agreement-close-button" 
            style={{ backgroundImage: getImg('xieyi_close_icon.png') }}
            onClick={onClose}
          />
          */}
          {/* Title */}
          <div className="agreement-modal-title">{t('welcome.agreementTitle', '協議')}</div>

          {/* Dropdown (Selection) */}
          <div className="agreement-dropdown-container" ref={dropdownRef}>
            <div className="agreement-dropdown-header" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
              <div className="agreement-search-bg" style={{ backgroundImage: getImg('xieyi_search_input_bg.png') }} />
              <div className="agreement-search-icon" style={{ backgroundImage: getImg('xieyi_search_icon.png') }} />
              <span className="agreement-header-text">
                {selectedId === 'all' ? t('common.all', '全部') : selectedAgreement?.title}
              </span>
            </div>

            {isDropdownOpen && (
              <div className="agreement-dropdown-list">
                <div 
                  className={`agreement-dropdown-item ${selectedId === 'all' ? 'selected' : ''}`}
                  onClick={() => handleAgreementSelect('all')}
                >
                  {t('common.all', '全部')}
                </div>
                {agreements.map(agreement => (
                  <div 
                    key={agreement.id} 
                    className={`agreement-dropdown-item ${agreement.id === selectedId ? 'selected' : ''}`}
                    onClick={() => handleAgreementSelect(agreement.id)}
                  >
                    {agreement.title}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div 
            className="agreement-text-content" 
            ref={contentRef} 
            onScroll={handleScroll}
          >
            {loading ? (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>{t('common.loading', 'Loading...')}</div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: displayContent || '' }} />
            )}
          </div>

          {/* Action Buttons */}
          <div className="agreement-modal-actions">
            <div
              className={`agreement-btn-agree ${!canAgree ? 'disabled' : ''}`}
              onClick={handleAgree}
              style={{ 
                backgroundImage: getBtnImg('btn-reject.png'),
                filter: !canAgree ? 'grayscale(100%)' : 'none',
                opacity: !canAgree ? 0.6 : 1,
                cursor: !canAgree ? 'not-allowed' : 'pointer'
              }}
            >
              <span className="agreement-btn-text">{t('common.agree', '同意')}</span>
            </div>
            <div
              className="agreement-btn-reject"
              onClick={handleReject}
              style={{ backgroundImage: getBtnImg('btn-reject.png') }}
            >
              <span className="agreement-btn-text">{t('common.disagree', '不同意')}</span>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        isOpen={showRejectConfirm}
        message={t('welcome.confirmExit', '是否確認退出。。。？')}
        onConfirm={confirmReject}
        onCancel={() => setShowRejectConfirm(false)}
      />
    </div>
  );
};

export default AgreementModal;
